import { alpha } from '@mui/material/styles';
import { inputBaseClasses } from '@mui/material/InputBase';
import { inputLabelClasses } from '@mui/material/InputLabel';
import { filledInputClasses } from '@mui/material/FilledInput';
import { outlinedInputClasses } from '@mui/material/OutlinedInput';

// ----------------------------------------------------------------------

export default function TextField(theme) {
  const color = {
    focused: theme.palette.primary.main,
    active: theme.palette.text.secondary,
    placeholder: theme.palette.text.disabled,
  };

  const font = {
    label: theme.typography.body1,
    value: theme.typography.body2,
  };

  return {
    // HELPER
    MuiFormHelperText: {
      defaultProps: {
        component: 'div',
      },
      styleOverrides: {
        root: {
          margin: 0,
        },
      },
    },

    // LABEL
    MuiFormLabel: {
      styleOverrides: {
        root: {
          [`&.${inputLabelClasses.shrink}`]: {
            color: color.active,
            top: -12,
            fontSize: '0.875rem',
            fontWeight: 700,
            lineHeight: 1.5,

            [`&.${inputLabelClasses.focused}`]: {
              color: color.focused,
            },
            [`&.${inputLabelClasses.error}`]: {
              color: theme.palette.error.main,
            },
            [`&.${inputLabelClasses.disabled}`]: {
              color: theme.palette.text.disabled,
            },
            [`&.${inputLabelClasses.filled}`]: {
              transform: 'translate(12px, 6px) scale(0.75)',
            },
          },
        },
      },
    },

    // BASE
    MuiInputBase: {
      styleOverrides: {
        root: {
          backgroundColor: 'white',
          [`&.${inputBaseClasses.disabled}`]: {
            '& svg': {
              color: theme.palette.grey[300],
              stroke: theme.palette.grey[300],
            },
          },
        },
        input: {
          ...font.value,
          height: 21,
          padding: '12px 16px !important',
          fontWeight: 500,
          lineHeight: '150%',
          letterSpacing: '-0.14px',
          '&::placeholder': {
            opacity: 1,
            color: color.placeholder,
          },
          [theme.breakpoints.down('sm')]: {
            // Below 16px, iOS Safari auto-zooms in on focus and can get
            // stuck zoomed in after the field loses focus.
            fontSize: '1rem',
          },
        },
      },
    },

    // STANDARD
    MuiInput: {
      styleOverrides: {
        underline: {
          '&:before': {
            borderBottomColor: alpha(theme.palette.grey[500], 0.32),
          },
          '&:after': {
            borderBottomColor: color.focused,
          },
        },
      },
    },

    // OUTLINED
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          boxSizing: 'border-box',
          border: `1px solid ${theme.palette.divider}`,
          fieldset: { border: 'none' },
          [`&.${outlinedInputClasses.focused}`]: {
            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: color.focused,
            },
            borderColor: color.focused,
          },
          [`&.${outlinedInputClasses.error}`]: {
            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: theme.palette.error.main,
            },
          },
          [`&.${outlinedInputClasses.disabled}`]: {
            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: theme.palette.action.disabledBackground,
            },
            'input, textarea': {
              border: 'none',
              color: theme.palette.grey[900],
              '-webkit-text-fill-color': theme.palette.grey[900],
              fontWeight: 500,
              fontSize: '0.875rem',
              lineHeight: 1.5,
              letterSpacing: '-1%',
            },
            backgroundColor: theme.palette.grey[50],
            border: '1px solid',
            borderColor: theme.palette.grey[100],
          },
        },
        notchedOutline: {
          borderColor: alpha(theme.palette.grey[500], 0.2),
          transition: theme.transitions.create(['border-color'], {
            duration: theme.transitions.duration.shortest,
          }),
        },
        input: {
          '&:-webkit-autofill': {
            WebkitBoxShadow: '0 0 0 1000px #fff inset',
          },
        },
      },
    },

    // FILLED
    MuiFilledInput: {
      defaultProps: {
        disableUnderline: true,
      },
      styleOverrides: {
        root: {
          borderRadius: theme.shape.borderRadius,
          backgroundColor: alpha(theme.palette.grey[500], 0.08),
          '&:hover': {
            backgroundColor: alpha(theme.palette.grey[500], 0.16),
          },
          [`&.${filledInputClasses.focused}`]: {
            backgroundColor: alpha(theme.palette.grey[500], 0.16),
          },
          [`&.${filledInputClasses.error}`]: {
            backgroundColor: alpha(theme.palette.error.main, 0.08),
            [`&.${filledInputClasses.focused}`]: {
              backgroundColor: alpha(theme.palette.error.main, 0.16),
            },
          },
          [`&.${filledInputClasses.disabled}`]: {
            backgroundColor: theme.palette.action.disabledBackground,
          },
        },
      },
    },

    // MuiTextField: {
    //   styleOverrides: {
    //     root: {
    //       [`&.Mui-disabled`]: {
    //         backgroundColor: 'red',
    //       },
    //     },
    //   },
    // },

    // DISABLED
    Mui: {
      styleOverrides: {
        disabled: {
          backgroundColor: theme.palette.grey[50],
        },
      },
    },
  };
}
